// §V · PATTERN COMBINATIONS (the patterns) — ported from the Claude Design canon
// `R10 - §V - canon F5.html` (project dd7b95d4). NB the emitted `data-canon` attribute reads
// "R10-§V-F4" — CD did not bump it when issuing F5, so it is part of the frozen byte-parity
// output and must NOT be "corrected" (doing so breaks the golden). Layout =
// R9 §V (styles/r10-v.css; byte-checked by css-parity-v.test.js).
//
// Unlike §IV (one chart), §V is a CARD CATALOG: a MAIN PATTERN banner over a list of
// combination cards, each = number + italic_title + multi-paragraph description + a
// mini-chart. §V is SELF-MANAGED like §II/§III/§IV: it renders its OWN artboard shell
// (the <section class="artboard patterns-page"> with the §V runner + "v · pairs" footer),
// so it is mounted WITHOUT the generic Artboard wrapper. It is board-driven (a `view`).
//
// The card COPY is the §V Combinations Catalog (37219875…): Section C written cells (CELL)
// and Section F stub templates (STUB) are transcribed VERBATIM — CC reads the bank, does
// not author it. The banner name (patternName), the cell selection (cardCopy), the
// mini-chart geometry (miniChart) and the mean reference (meanRef) are deterministic
// generators run over the board data — a faithful re-implementation of CD's page(v). The
// whole section body is emitted through the same string builders CD used and mounted via
// dangerouslySetInnerHTML, so the DOM is byte-identical to the canon (svg-parity-v).
//
// The client re-fit (fitSheet) does not run under SSR; where CD's real browser triggered
// it (board v14: both concession stages), the board declares `fit` and the section renders
// it statically — exactly §IV's labelMode contract. The corpus is single-axis (29 bios
// move only Security); the frequency map is blind to non-Security classes — that is the
// corpus, not a threshold to tune. Detector is not touched here.

import { patternName as patternNameOf, MECH } from '../../copy/chartCopy.js';
import { CELL_A31 } from '../../copy/passportCopy.js';

const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII', 'XIII', 'XIV', 'XV'];
// cc-30: the table stops at XV (the wizard caps at 15 stages), but nothing downstream did — an
// imported 16-stage map printed the literal 'undefined' as the ember NOW tick of every mini-chart
// and left a dangling separator in the evidence line. §III and §VI already fall back to a
// decimal; §V did not. Same idiom as theirs.
const roman = (i) => ROMAN[i] ?? String(i + 1);
const NUMW = { 0: 'zero', 1: 'one', 2: 'two', 3: 'three', 4: 'four', 5: 'five', 6: 'six', 7: 'seven', 8: 'eight', 9: 'nine', 10: 'ten', 11: 'eleven', 12: 'twelve', 13: 'thirteen', 14: 'fourteen', 15: 'fifteen' };
// layer palette — canon A.8, same hex as §III/§IV (universal rule #6)
const L = {
  Vitality: { name: 'vitality', color: '#D48A3C', dash: null },
  Security: { name: 'security', color: '#C7BFAE', dash: null },
  Growth: { name: 'growth', color: '#8FA168', dash: null },
  Authorship: { name: 'authorship', color: '#C7BFAE', dash: '6 4' },
  Wanting: { name: 'wanting', color: '#A86520', dash: null },
  Yourself: { name: 'being yourself', color: '#8FA168', dash: '6 4' },
  Lasting: { name: 'wanted to last', color: '#C7BFAE', dash: '1.5 4' },
};
const lname = (k) => L[k].name;
const ALL7 = ['Vitality', 'Security', 'Growth', 'Authorship', 'Wanting', 'Yourself', 'Lasting'];

// ================= Layer glossary (Catalog reference, s175–s179) =================
const GLOSS = {
  Vitality: '<b>vitality is the daily energy you have to live on.</b> the body issues a fresh supply each morning — you can’t save it, you can’t borrow tomorrow’s. on a full day, you handle the day without thinking about it. on a drained day, even small things start to cost you.',
  Security: '<b>security is whether the ground under you feels like it will hold.</b> on solid ground, you move through the day with your guard down — you rest easy, you take risks, you can be still. hung by a thread, you brace — sleep goes thin, small surprises read as threats, rest stops being possible.',
  Growth: '<b>growth is when the complexity of what you can handle with ease keeps increasing.</b> you take on new skills, see situations from angles you couldn’t before, find yourself in conversations you couldn’t have had a year ago. without growth, you’re sliding back — the challenges keep growing, and you don’t.',
  Authorship: '<b>authorship is an act.</b> you make a choice and you own it. not a reaction to circumstances, not autopilot — your decision. you go after what you want, and that’s how you become the author of your own life.',
  Wanting: '<b>wanting is the live pull toward something specific</b> — something you can name, that you actually want, not just what makes sense. when it’s there, "what do you want" gets a real answer. when it’s gone, you keep moving — but you couldn’t name what you’re moving toward.',
  Yourself: '<b>being yourself is a state.</b> it means living from your internal standard — what you value, what you believe, where your line is. no one else gets to tell you who you should be.',
  Lasting: '<b>wanted to last is whether you wanted the experience to keep going.</b> when you do, you’re in the middle of something and you don’t want it to end — you’d live it again if you could. when you don’t, you’re in the middle of something you don’t want to keep going — you wouldn’t live it again if you could.',
};

// ================= Section C · written cells (verbatim) =================
const CELL = {
  'A.1.1': { h: 'vitality and security moved as one curve.', p: [GLOSS.Vitality, GLOSS.Security,
    '<b>security is the ground. vitality is what runs on it.</b>',
    'when the day’s energy drops, look at the ground. that’s what feeds it.'] },
  'A.1.16': { h: 'wanting and authorship moved as one curve.', p: [GLOSS.Wanting, GLOSS.Authorship,
    '<b>knowing what you want without going after it isn’t wanting. it’s a wish. going after what you don’t actually want isn’t authorship. it’s imitation.</b>'] },
  'A.1.19': { h: 'wanting and being yourself moved as one curve.', p: [GLOSS.Wanting, GLOSS.Yourself,
    '<b>wanting comes from being yourself. take away one, the other goes too.</b>',
    'wanting what others expect of you — instead of what’s actually yours — isn’t wanting. it’s pleasing.'] },
  'A.2.2.1': { h: 'security stands apart from the rest.', p: [GLOSS.Security,
    'right now, security is the one line apart from the rest.',
    '<b>when it sits above the rest,</b> you’re buying it by paying with the others. if that’s been happening without you choosing it, this is where you see it.',
    '<b>when it sits below the rest,</b> you’ve been trying to make up for the missing ground through the others. they can’t do that job.'] },
  'A.2.6.1': { h: 'being yourself stands apart from the rest.', p: [GLOSS.Yourself,
    'right now, being yourself is the one line apart from the rest.',
    '<b>when it sits above the rest,</b> you wouldn’t sacrifice yourself, no matter what. the rest paid for it.',
    '<b>when it sits below the rest,</b> life is running — but it’s not yours. you do what’s expected of you, at the cost of yourself.'] },
  // единственная ячейка ряда с position-conditional рендером (s196)
  'A.2.7.1:above': { h: 'wanted to last stands apart from the rest.', p: [GLOSS.Lasting,
    'right now, lasting is the one line apart from the rest.<br>what about that stage would you live again?'] },
  'A.2.7.1:below': { h: 'wanted to last stands apart from the rest.', p: [GLOSS.Lasting,
    'right now, lasting is the one line apart from the rest.<br>what about that stage wouldn’t you live again?'] },
  'A.2.4.2': { h: 'you were the author once — and gradually you stopped.', p: [
    'authorship is an act. you make a choice and you own it. not a reaction to circumstances, not autopilot — your decision. you go after what you want, and that’s how you become the author of your own life.',
    'there was a period when you were doing exactly that — making the choices, taking the responsibility. whatever you were doing then — that was authorship. not luck, not circumstance. and that was the same person you are now.'] },
  // A.3.1 RETIRED (8б): жёстко вписанная тройка снята — см. cellA31() (общая ячейка «moving as one» со слотами).
  'A.3.2': { h: 'a life stretched between two poles.', p: [
    'several layers held high across the stages. several others held low. the gap between them stayed steady — you’ve been stretched between two poles.',
    'one part of your life sits in the high group. on the surface there is little to fault — there are things to be proud of, and many of the markers of "a good life" are in place.',
    'the other part sits in the low group, and it’s a hole the high group feeds into. the energy you build up there drains down here. attention pulls back to it, away from what’s working — because this part needs your presence too.',
    'this stretch has a cause. finding it is what makes coming off it possible.'] },
  'A.4.2': { h: 'this isn’t several separate stories of falling apart. it’s one.', p: [
    'the lines went down at the same time — along the same trajectory — and came back together too. if you’ve ever tried to explain that period and ended up listing separate things, this is why. it wasn’t several. it was one event that took everything down at once.',
    'when one layer falls, the rest holds. when everything falls, there’s nothing to hold. the one who came back isn’t the one who went down. when the whole field falls together, what comes back tends to be something new.'] },
};

// ================= Section F · stubs (slot templates) =================
const STUB = {
  'F.1': (s) => ({ h: `${s.a} and ${s.b} moved as one curve.`, p: [s.stemA, s.stemB,
    `two layers, identical values across ${s.N} stages. when two layers move as one, they share a lever — move one and the other comes with it. which one can you reach first?`] }),
  'F.2': (s) => ({ h: `${s.a} and ${s.b} started together, then split.`, p: [s.stemA, s.stemB,
    `for a while these two moved together. somewhere along the way they pulled apart. ${s.N} stages later there’s daylight between them.`,
    'at some point one started moving differently from the other. find the stage where that turn began — that’s where the change lives.'] }),
  'F.3.spatial': (s) => ({ h: `${s.layer} out of step`, p: [s.stem,
    `across your stages, ${s.layer} hasn’t moved with the rest. the others moved on together; this one stayed where it was.`,
    'this is a shape, not random. something acts on this line that doesn’t act on the others. what makes it different from the rest?'] }),
  'F.3.temporal': (s) => ({ h: `${s.layer} held the longest`, p: [s.stem,
    `${s.layer} held its ground after the others gave way. it stayed up longest, then dropped too. now the whole field sits low.`,
    'this is a shape, not random. holding it up that long had a reason. what were you holding it up for?'] }),
  'F.5.active-rebuild': (s) => ({ h: `${s.layer} fell — then it came back.`, p: [s.stem,
    `at some point, ${s.layer} fell. much of the rest of your life held while ${s.layer} was down. it’s back now — but what came back isn’t what was there before. it carries the fall in it.`] }),
  'F.5.biological': (s) => ({ h: `${s.layer} fell — then it came back.`, p: [s.stem,
    `at some point, ${s.layer} fell. much of the rest of your life held while ${s.layer} was down. it’s back now. and you carry what it was like to lose it.`] }),
  'F.6': (s) => ({ h: `${s.layer} cycling`, p: [s.stem,
    `right now, ${s.layer} is the one line that doesn’t hold steady. it goes up. then down. then up again. then down.`,
    `each high was real, even though it ended. this is a shape — there’s something repeating in it. at each high, something brought ${s.layer} up. at each low, something brought it down — or you did something that did. what’s the same each time?`] }),
  'F.7': () => ({ h: MECH.scatter, p: [
    'the lines on the page sit close to each other. no single one is pulling away. no pair has locked together. no beat is repeating across the layers.',
    'the weight is spread across these lines. either nothing is asking for more than the rest — or you’re not letting it. this is a shape, not nothing. is this how things sit for you, or does something still stand out?'] }),
};

// ---- cell selection: the engine hands `cell` directly for every type (current-singleton
// L1..L7 → A.2.1.1 … A.2.7.1). Read card.cell as-is; the only local resolves are the
// pair-row (A.1 → pair-specific) and the position-conditional A.2.7.1 dir. ----
// keyed by the ALPHABETICALLY-SORTED member pair so the lookup is order-independent
// (the engine may emit card.layers in either order; a reversed pair must still resolve).
const PAIR_CELL = { 'Security+Vitality': 'A.1.1', 'Authorship+Wanting': 'A.1.16', 'Wanting+Yourself': 'A.1.19', 'Authorship+Growth': 'A.1.12', 'Vitality+Yourself': 'A.1.5' };
// A.3.1 «moving as one» — общая ячейка со слотами состава и числа (решение web-Claude, 8б).
// Жёстко вписанная редакция снята: её довод был завязан на wanting/authorship/being yourself,
// а движок кладёт в triplet-as-one группы 3–7 любых слоёв (текст врал по числу и содержанию).
// Одна ветка с подстановкой, не шаблон-на-тип — s354.d2 не нарушен. Заголовок слоёв не называет.
function cellA31(k) {
  const N = NUMW[k.length] || k.length, m = k.map(lname);
  // defensive: an empty/degenerate layer list must never leak the literal "undefined"
  // into the prose (the join branch indexes m[m.length-1]). Parity boards always feed ≥3.
  const layers = m.length > 1 ? `${m.slice(0, -1).join(', ')} and ${m[m.length - 1]}` : (m[0] || '');
  return { h: CELL_A31.h, p: [CELL_A31.p(N, layers)] };
}
function cardCopy(card, v) {
  const k = card.layers, N = NUMW[v.N] || v.N;
  if (card.stub) {
    // defensive: a stub key with no template in the STUB bank (typo, or a new detector
    // stub not yet transcribed) must NOT crash the whole §V page — render the missing
    // guard card instead. Parity boards only ever carry known stubs.
    const tpl = STUB[card.stub];
    if (!tpl) return { src: card.stub, stub: true, h: '', p: [], missing: true };
    const s = { layer: lname(k[0]), stem: GLOSS[k[0]], a: lname(k[0]), b: k[1] ? lname(k[1]) : '', stemA: GLOSS[k[0]], stemB: k[1] ? GLOSS[k[1]] : '', N };
    return Object.assign({ src: card.stub, stub: true }, tpl(s));
  }
  let id = card.cell;
  if (!id || id === 'A.1') {
    if (card.type === 'parallel-whole') id = PAIR_CELL[[...k].sort().join('+')];
  }
  if (id === 'A.2.7.1') id = 'A.2.7.1:' + (card.dir || 'above');
  if (id === 'A.3.1') return Object.assign({ src: 'A.3.1', stub: false }, cellA31(k));
  const c = CELL[id];
  if (!c) return { src: id || '—', stub: false, h: '', p: [], missing: true };
  return Object.assign({ src: id, stub: false }, c);
}

// ================= banner · pattern_name (the one axis-A field) =================
// `MECH`/`patternName` вынесены в общий дом `src/copy/chartCopy.js` (B8-1): их читает и overlay
// шага 6. Печатное имя слоя у §V своё — передаётся параметром.
const patternName = (v) => patternNameOf(v, lname);

// ================= mini-chart =================
const MW = 380, MH = 130, MYT = 14, MYB = MH - 22, MYM = (MYT + MYB) / 2;
function smooth(pts, t = 0.5) {
  if (pts.length < 2) return pts.length ? `M ${pts[0].x.toFixed(1)},${pts[0].y.toFixed(1)}` : '';
  let d = `M ${pts[0].x.toFixed(1)},${pts[0].y.toFixed(1)}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] || pts[i], p1 = pts[i], p2 = pts[i + 1], p3 = pts[i + 2] || p2;
    d += ` C ${(p1.x + (p2.x - p0.x) * (t / 3)).toFixed(1)},${(p1.y + (p2.y - p0.y) * (t / 3)).toFixed(1)} ${(p2.x - (p3.x - p1.x) * (t / 3)).toFixed(1)},${(p2.y - (p3.y - p1.y) * (t / 3)).toFixed(1)} ${p2.x.toFixed(1)},${p2.y.toFixed(1)}`;
  }
  return d;
}
// mean-reference for divergence: cluster of touched-moving-together, outlier excluded
// (rule s236; no engine key — computed at assembly)
function meanRef(v, involved) {
  const rest = ALL7.filter((k) => v.layers[k] && !involved.includes(k));
  if (rest.length < 2) return null;
  const dev = rest.map((k) => {
    const vals = v.layers[k], mu = vals.reduce((a, b) => a + b, 0) / vals.length;
    return { k, vals, spread: Math.max(...vals) - Math.min(...vals), mu };
  });
  const grand = dev.reduce((a, d) => a + d.mu, 0) / dev.length;
  const out = dev.slice().sort((a, b) => Math.abs(b.mu - grand) - Math.abs(a.mu - grand))[0];
  const cluster = dev.length > 2 ? dev.filter((d) => d !== out) : dev;
  return { keys: cluster.map((d) => d.k), vals: v.layers[cluster[0].k].map((_, i) => cluster.reduce((a, d) => a + d.vals[i], 0) / cluster.length) };
}
function miniChart(v, card, idx) {
  const N = v.N, xs = Array.from({ length: N }, (_, i) => 18 + (N === 1 ? 0 : (i / (N - 1)) * (MW - 36)));
  const yVal = (x) => MYM - (x / 3) * ((MYB - MYT) / 2);
  const pts = (vals) => vals.map((x, i) => ({ x: xs[i], y: yVal(x) }));
  const involved = card.layers.filter((k) => v.layers[k]);
  const mean = (card.type === 'divergence-over-time') ? meanRef(v, involved) : null;
  const seps = xs.slice(0, -1).map((x, i) => `<line x1="${((x + xs[i + 1]) / 2).toFixed(1)}" y1="${MYT - 4}" x2="${((x + xs[i + 1]) / 2).toFixed(1)}" y2="${MYB + 4}" stroke="#1C1E22" stroke-width=".5"></line>`).join('');
  const curves = involved.map((k) => {
    const c = L[k], p = pts(v.layers[k]);
    const dots = p.map((q) => `${c.color === '#C7BFAE' ? `<circle cx="${q.x.toFixed(1)}" cy="${q.y.toFixed(1)}" r="4.4" fill="#0a0b0d"></circle>` : ''}<circle cx="${q.x.toFixed(1)}" cy="${q.y.toFixed(1)}" r="3.2" fill="${c.color}"></circle>`).join('');
    return `<g data-layer="${k}"><path d="${smooth(p)}" fill="none" stroke="${c.color}" stroke-width="${c.dash ? 2 : 2.4}"${c.dash ? ` stroke-dasharray="${c.dash}"` : ''} stroke-linecap="round" stroke-linejoin="round"></path>${dots}</g>`;
  }).join('');
  const meanPath = mean ? `<path d="${smooth(pts(mean.vals))}" fill="none" stroke="#5A554A" stroke-width="1.4" stroke-dasharray="4 3" stroke-linecap="round"></path>` : '';
  const axis = xs.map((x, i) => `<text x="${x.toFixed(1)}" y="${MYB + 14}" text-anchor="middle" font-family="JetBrains Mono, monospace" font-size="${N > 8 ? 8.5 : 10}" letter-spacing=".16em" fill="${i === N - 1 ? '#C8482B' : '#8F887A'}">${roman(i)}</text>`).join('');
  const legend = involved.map((k) => {
    const c = L[k], sw = c.dash ? `repeating-linear-gradient(to right, ${c.color} 0 ${c.dash.split(' ')[0]}px, transparent ${c.dash.split(' ')[0]}px ${(+c.dash.split(' ')[0]) + (+c.dash.split(' ')[1])}px)` : c.color;
    return `<span class="pt-card-leg-item"><span class="pt-card-leg-sw" style="background:${sw}"></span><em>${lname(k)}</em></span>`;
  }).join('') + (mean ? `<span class="pt-card-leg-item pt-card-leg-mean"><span class="pt-card-leg-sw" style="background:repeating-linear-gradient(to right,#5A554A 0 4px,transparent 4px 7px)"></span><em>mean of ${mean.keys.map(lname).join(' + ')}</em></span>` : '');
  return `<div class="pt-card-chart">
<svg class="pt-mini" data-field="combination_cards[${idx}].mini_chart" viewBox="0 0 ${MW} ${MH}" preserveAspectRatio="none">
<defs><linearGradient id="ptn-${v.id}-${idx}" x1="0" x2="1" y1="0" y2="0"><stop offset="0" stop-color="#7A1E10" stop-opacity="0"></stop><stop offset="1" stop-color="#7A1E10" stop-opacity=".22"></stop></linearGradient></defs>
<rect x="${(xs[N - 1] - (N > 1 ? (xs[N - 1] - xs[N - 2]) / 2 : 20)).toFixed(1)}" y="${MYT - 4}" width="${(N > 1 ? (xs[N - 1] - xs[N - 2]) / 2 : 20) + 22}" height="${MYB - MYT + 8}" fill="url(#ptn-${v.id}-${idx})"></rect>
<line x1="0" y1="${yVal(0)}" x2="${MW}" y2="${yVal(0)}" stroke="rgba(143,136,122,.20)" stroke-dasharray="2 5" stroke-width=".7"></line>
${seps}${meanPath}${curves}
<line x1="${xs[N - 1].toFixed(1)}" y1="${MYT - 6}" x2="${xs[N - 1].toFixed(1)}" y2="${MYB + 2}" stroke="#C8482B" stroke-width=".9" stroke-dasharray="3 3"></line>
${axis}
</svg>
<div class="pt-card-legend">${legend}</div></div>`;
}

/**
 * Cards whose copy actually exists (cc-30).
 *
 * `cardCopy` returns `{ h: '', p: [], missing: true }` for a cell or stub the bank does not
 * define. That branch was written so an unknown key could not crash the page — and it does not.
 * What it did instead was DRAW the card: number, mini-chart, and the visible `cell · A.1.12`
 * badge, with no words on it. The engine names far more cells than the bank defines (16
 * parallel-whole pairs against 5 mapped, of which 2 point at ids `CELL` does not hold; all seven
 * current-singletons against 3), so this was live on 14 of the 29 bench biographies.
 *
 * Filtering happens HERE and not in `viewV` because only this file knows the bank. It runs
 * BEFORE numbering, so "01 of 03" counts the cards a reader can actually read.
 *
 * A card that drops is not hidden information: there is nothing written to hide. If nothing
 * resolves the section falls to the existing `absent` state — zero cards is already a canon
 * state (boards v08/v16), and every surviving count (1–4) exists in canon too, so no new
 * geometry appears. No-op on the boards: all 16 resolve.
 */
function resolvedCards(v) {
  return (v.cards || []).filter((c) => !cardCopy(c, v).missing);
}

// ================= page =================
function card(v, c, j, M) {
  const copy = cardCopy(c, v);
  const body = copy.p.map((p) => `<p class="pt-card-p">${p}</p>`).join('');
  return `<article class="pt-card${copy.stub ? ' pt-card-stub' : ''}" data-field="combination_cards[${j}]" data-type="${c.type}"${copy.stub ? ` data-stub="${copy.src}"` : ''}>
<span class="pt-card-src">${copy.stub ? 'stub · ' : 'cell · '}${copy.src}</span>
<div class="pt-card-num" data-field="combination_cards[j].number_tag"><span class="pt-card-no">${String(j + 1).padStart(2, '0')}</span><span class="pt-card-of">of ${String(M).padStart(2, '0')}</span></div>
<h3 class="pt-card-title" data-field="combination_cards[j].italic_title">${copy.h}</h3>
<div class="pt-card-body" data-field="combination_cards[j].description">${body}</div>
${miniChart(v, c, j)}
</article>`;
}
// everything INSIDE the <section> — the outer <section> element (with its data-* + fit
// classes) is the React host; this returns its innerHTML, byte-identical to CD's page(v).
function pageInner(v) {
  const M = resolvedCards(v).length, N = v.N;
  const pn = patternName(v);
  const stages = v.carrierStages.map(roman).join(' · ');
  return `
<div class="ab-paper"></div><div class="ab-grain"></div>
<header class="ab-runner">
  <span data-field="section_header_left"><span class="runner-roman">V</span>pattern combinations</span>
  <span class="runner-meta" data-field="section_header_right">life map · the reading</span>
</header>
<div class="pt-sheet">
  <div class="pt-hd">
    <div class="pt-roman">V</div>
    <h2 class="pt-title" data-field="page_title"><span class="pt-em">where the layers</span><span>tell the same story.</span></h2>
    <div class="pt-meta" data-field="title_side_meta_top"><b>pattern</b><span class="sep">·</span><span>long arc</span>${(M && N) ? `<span class="sep">·</span><span class="num">${M}<em>/</em>${N}</span>` : ''}</div>
  </div>
  <p class="pt-sub" data-field="intro_paragraph">one pattern is named. beneath it, the combinations — pairs and singletons whose shapes carry the same finding from different angles. read them in order — they build.</p>
  <div class="pt-main" data-field="main_pattern_banner">
    <div class="pt-main-tag" data-field="main_pattern_banner.sidebar_tag">main pattern</div>
    <div class="pt-main-body">
      <div class="pt-main-meta" data-field="main_pattern_banner.metadata">detected</div>
      <div class="pt-main-name" data-field="main_pattern_banner.pattern_name"${pn.flag ? ` data-flag="${pn.flag}"` : ''}>${pn.t}</div>
      ${stages ? `<div class="pt-main-evidence" data-field="main_pattern_banner.evidence_stages"><b>evidence</b> stages ${stages}</div>` : ''}
    </div>
  </div>
  <div class="pt-comb-head">
    <div class="pt-comb-k" data-field="combinations_subhead.label">combinations</div>
    <div class="pt-comb-dx" data-field="combinations_subhead.intro">each card pairs the curves with the finding the geometry supports.</div>
  </div>
  <div class="pt-list">${resolvedCards(v).map((c, j) => card(v, c, j, M)).join('')}</div>
</div>
<footer class="ab-foot">
  <span data-field="footer_left"><span class="foot-em">life map</span> <span class="foot-faint">·</span> pattern combinations</span>
  <span class="foot-page" data-field="footer_right">v · pairs</span>
</footer>
`;
}

// ================= §V removed · flat field / no signal (canon F4) =================
// F4 (s414 §V empty-degrade): when the input is truly flat, §V is REMOVED (not a false
// "moving as one" card on flat data) and the document renumbers — the §VI section_absent
// model, not transport_failed. The trigger is a spec/detector question, marked provisional.
// This returns the section INNER (host <section> is the React element below).
const V_TRIGGER = 'zero_spread_all_layers';
function absentPageInner(v) {
  const cascade = !!v.cascade;
  const rows = cascade
    ? [['§V · весь раздел', '<del>pattern combinations</del><ins>не рендерится</ins>'],
      ['§VI · весь раздел', '<del>evidence</del><ins>не рендерится</ins><span>порог &lt; 2 цитат</span>'],
      ['§VII · section_header_left', '<del>vii closing</del><ins>v closing</ins>'],
      ['§VII · footer_right', '<del>vii · closing</del><ins>v · closing</ins>'],
      ['mainPattern · footerCount', '<ins>none · 0</ins>']]
    : [['§V · весь раздел', '<del>pattern combinations</del><ins>не рендерится</ins>'],
      ['§VI · section_header_left', '<del>vi evidence</del><ins>v evidence</ins>'],
      ['§VI · footer_right', '<del>vi · transcript</del><ins>v · transcript</ins>'],
      ['§VII · section_header_left', '<del>vii closing</del><ins>vi closing</ins>'],
      ['§VII · footer_right', '<del>vii · closing</del><ins>vi · closing</ins>'],
      ['mainPattern', '<ins>none</ins><span>карточек нет, баннер не печатается</span>']];
  const head = cascade
    ? "the layers sat level with each other, and nothing was said in your own words — nothing to combine, nothing to quote."
    : "the layers sat level with each other — nothing paired, nothing pulled apart. there's no combination to name here.";
  const note = cascade
    ? 'Каскад: §V снят по отсутствию сигнала <b>и</b> §VI снят по порогу &lt; 2 цитат. Перенумерация складывается — §VII встаёт на место §V. Борд нужен порту, потому что оба состояния приходят с одного и того же пустого ввода: сначала считается §V, потом §VI, сдвиг применяется один раз к остатку документа.'
    : 'Триггер (<b>провизор</b>, ждём спеку/детектор): раздел снимается, когда детектор не отдаёт ни одной combination-карточки <b>и</b> спред всех семи слоёв равен нулю — <code>' + V_TRIGGER + '</code>. Что <em>не</em> триггер: <b>scatter</b> (борд 06) — там сигнал есть, он просто разнонаправленный, карточка «an even spread» остаётся; и одиночный слабый слой (борд 15) — раздел печатается. До F3 на этом вводе печаталась карточка «moving as one»: семь плоских линий — не «движутся как один», а отсутствие сигнала, и утверждать обратное про пользователя нельзя.';
  return `
<div class="ab-paper"></div><div class="ab-grain"></div>
<header class="ab-runner">
  <span data-field="section_header_left"><span class="runner-roman">V</span>pattern combinations</span>
  <span class="runner-meta" data-field="section_header_right">life map · the reading</span>
</header>
<div class="pt-sheet"><div class="pt-absent${cascade ? ' pt-cascade' : ''}">
  <div class="pt-absent-k" data-field="section_absent.label">§V · not rendered</div>
  <div class="pt-absent-h" data-field="section_absent.heading">${head}</div>
  <div class="pt-absent-list">${rows.map((r) => `<div class="pt-absent-row"><div class="pt-absent-f">${r[0]}</div><div class="pt-absent-v">${r[1]}</div></div>`).join('')}</div>
  <p class="pt-absent-note" data-field="section_absent.note">${note}</p>
</div></div>
<footer class="ab-foot">
  <span data-field="footer_left"><span class="foot-em">life map</span> <span class="foot-faint">·</span> pattern combinations</span>
  <span class="foot-page" data-field="footer_right">v · pairs</span>
</footer>
`;
}

export default function SectionV({ view }) {
  const v = view;
  // F4: a truly-flat board is REMOVED — render the section_absent state, not a card.
  // cc-30 extends the same door: if the engine named cards but NONE of them has copy in the
  // bank, the page would be a banner over an empty list. Zero cards is already the absent state
  // (boards v08/v16), so it takes that door rather than inventing a third one. The engine's own
  // verdict still wins — `v.absent` is checked first, and its cascade flag is preserved.
  if (v.absent || ((v.cards || []).length > 0 && resolvedCards(v).length === 0)) {
    const cascade = !!v.cascade;
    return (
      <section
        className="artboard patterns-page"
        data-screen-label={v.screen}
        data-run={v.source}
        data-canon="R10-§V-F4"
        data-degradation={cascade ? 'section_absent:cascade' : 'section_absent'}
        data-provisional={`empty_trigger:${V_TRIGGER}`}
        data-card-count="0"
        data-stage-count={v.N}
        dangerouslySetInnerHTML={{ __html: absentPageInner(v) }}
      />
    );
  }
  const M = resolvedCards(v).length;
  // fit is baked (not computed): CD's real-browser fitSheet fires only on v14 (both
  // concession stages, no remainder). SSR renders the declared fit statically.
  const fit = v.fit || null;
  const className = 'artboard patterns-page'
    + (fit && fit.tight ? ' pt-fit-tight' : '')
    + (fit && fit.smaller ? ' pt-fit-smaller' : '');
  const style = fit && fit.miniH ? { '--mini-h': fit.miniH } : undefined;

  return (
    <section
      className={className}
      data-screen-label={v.screen}
      data-run={v.source}
      data-canon="R10-§V-F4"
      data-family={v.family}
      data-carrier-config={v.carrierConfig}
      data-card-count={M}
      data-stage-count={v.N}
      {...(fit && fit.note ? { 'data-fit': fit.note } : {})}
      {...(style ? { style } : {})}
      dangerouslySetInnerHTML={{ __html: pageInner(v) }}
    />
  );
}
